/**
 * "Email Issues" ticket category: ONE ticket about one mailbox problem, with a
 * "Type of Problem" (a subcategory of the category) and a few extra fields that
 * only some problem types need. Same shape of module as `email-deletion.ts`: the
 * single source of truth for the fields and their validation, used by the web
 * form (instant feedback) and by the API (the authoritative check).
 *
 * This is a SERVICE REQUEST only: nothing here resets a password, changes a
 * phone number or grants mailbox access. Passwords, MFA codes and recovery codes
 * are never asked for, and free text that looks like one is refused. Values are
 * PII: audit rows record the problem type code only and nothing logs them.
 *
 * Kept separate from Email Creation / Email Deletion on purpose — those two
 * categories and their workflows are unchanged.
 */
import { z } from 'zod';
/** Stable catalogue code of the category (names can be renamed by an admin; codes cannot). */
export const EMAIL_ISSUES_CATEGORY_CODE = 'EMAIL_ISSUES';
/**
 * The problem types seeded by migration `20261013100000_email_issues_category`
 * (subcategory `code` + default `name`). Admins may rename, deactivate or add
 * more in Admin → Catalogue; behaviour below is keyed on the code, so renames
 * are safe and an admin-added type simply gets the common fields.
 * `OTHER` is shown last by the form, whatever its name.
 */
export const EMAIL_ISSUE_TYPES = [
    { code: 'MAILBOX_EXTENSION', name: 'Mailbox Extension', description: 'Request an increase in mailbox storage quota' },
    { code: 'PASSWORD_RESET', name: 'Password Reset', description: 'Request assistance resetting an email account password' },
    { code: 'UPDATE_MOBILE', name: 'Update Mobile Number', description: 'Request an update to the mobile number associated with the email account' },
    { code: 'OUTLOOK_LOGIN', name: 'Outlook Login Issue', description: 'Unable to sign in to Outlook' },
    { code: 'CANNOT_SEND', name: 'Unable to Send Emails', description: 'Outgoing emails are failing' },
    { code: 'CANNOT_RECEIVE', name: 'Unable to Receive Emails', description: 'Incoming emails are not being received' },
    { code: 'SYNC', name: 'Email Sync Issue', description: 'Emails are not synchronizing between devices' },
    { code: 'ACCOUNT_LOCKED', name: 'Account Locked', description: 'Email account is locked or sign-in is blocked' },
    { code: 'MFA', name: 'MFA / Authenticator Issue', description: 'Problems with multifactor authentication' },
    { code: 'SHARED_MAILBOX', name: 'Shared Mailbox Access', description: 'Request access to or report issues with a shared mailbox' },
    { code: 'DISTRIBUTION_LIST', name: 'Distribution List Issue', description: 'Membership, permissions, or delivery problems' },
    { code: 'DELIVERY_FAILURE', name: 'Email Delivery Failure', description: 'Bounce-back messages or delivery errors' },
    { code: 'MISSING_EMAILS', name: 'Missing or Deleted Emails', description: 'Assistance locating or recovering emails' },
    { code: 'MAILBOX_PERMISSION', name: 'Mailbox Permission Issue', description: 'Mailbox delegation, access, or permission problems' },
    { code: 'SPAM_PHISHING', name: 'Spam / Phishing Email', description: 'Report suspicious, unwanted, or malicious emails' },
    { code: 'ARCHIVE', name: 'Email Archive Issue', description: 'Problems accessing or using archived emails' },
    { code: 'CONFIGURATION', name: 'Email Configuration Issue', description: 'Outlook profile, mobile email setup, or configuration issues' },
    { code: 'OTHER', name: 'Other Email Issue', description: 'Any email-related issue not covered above' },
];
/** Short help text for a problem type code, or undefined for an admin-added type. */
export function emailIssueTypeDescription(code) {
    return EMAIL_ISSUE_TYPES.find((t) => t.code === code)?.description;
}
export const EMAIL_ISSUE_ACCESS_LEVELS = ['READ', 'FULL_ACCESS', 'SEND_AS', 'SEND_ON_BEHALF'];
export const EMAIL_ISSUE_ACCESS_LABELS = {
    READ: 'Read only',
    FULL_ACCESS: 'Full access',
    SEND_AS: 'Send as',
    SEND_ON_BEHALF: 'Send on behalf',
};
export const EMAIL_ISSUE_FIELDS = [
    'affectedEmail',
    'currentQuota',
    'requestedQuota',
    'businessJustification',
    'reason',
    'newMobileNumber',
    'sharedMailbox',
    'accessRequested',
    'senderEmail',
    'recipientEmail',
    'occurredAt',
    'errorMessage',
];
export const EMAIL_ISSUE_FIELD_LABELS = {
    affectedEmail: 'Affected email address',
    currentQuota: 'Current mailbox size / quota',
    requestedQuota: 'Storage increase or quota required',
    businessJustification: 'Business justification',
    reason: 'Reason for request',
    newMobileNumber: 'New mobile number',
    sharedMailbox: 'Shared mailbox email address',
    accessRequested: 'Access requested',
    senderEmail: 'Sender email',
    recipientEmail: 'Recipient email',
    occurredAt: 'Approximate date and time',
    errorMessage: 'Error or bounce-back message',
};
export const EMAIL_ISSUE_MAX_LENGTHS = {
    affectedEmail: 254,
    currentQuota: 50,
    requestedQuota: 50,
    businessJustification: 1000,
    reason: 500,
    newMobileNumber: 20,
    sharedMailbox: 254,
    senderEmail: 254,
    recipientEmail: 254,
    errorMessage: 4000,
};
/**
 * Which extra fields each problem type shows. `affectedEmail` is common to every
 * type and always required. A type not listed here (including any type an admin
 * adds later) gets only the common fields. Fields not listed for the selected
 * type are refused, so a ticket never stores details its type does not explain.
 */
export const EMAIL_ISSUE_TYPE_FIELDS = {
    MAILBOX_EXTENSION: { required: ['requestedQuota', 'businessJustification'], optional: ['currentQuota'] },
    PASSWORD_RESET: { required: ['reason'] },
    UPDATE_MOBILE: { required: ['newMobileNumber', 'reason'] },
    SHARED_MAILBOX: { required: ['sharedMailbox', 'accessRequested', 'businessJustification'] },
    DELIVERY_FAILURE: { required: ['senderEmail', 'recipientEmail', 'occurredAt', 'errorMessage'] },
};
/** Extra (non-common) fields shown for a problem type, in display order. */
export function emailIssueFieldsFor(typeCode) {
    const spec = typeCode ? EMAIL_ISSUE_TYPE_FIELDS[typeCode] : undefined;
    if (!spec)
        return [];
    const wanted = new Set([...spec.required, ...(spec.optional ?? [])]);
    return EMAIL_ISSUE_FIELDS.filter((f) => f !== 'affectedEmail' && wanted.has(f));
}
export function emailIssueFieldRequired(typeCode, field) {
    if (field === 'affectedEmail')
        return true;
    return Boolean(typeCode && EMAIL_ISSUE_TYPE_FIELDS[typeCode]?.required.includes(field));
}
/** "Other Email Issue" needs a detailed description: at least this many characters. */
export const EMAIL_ISSUE_OTHER_MIN_DESCRIPTION = 30;
const EMAIL_RE = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]{2,}$/;
/** Digits with an optional leading + and common separators; 7–15 digits in total. */
const MOBILE_RE = /^\+?[0-9][0-9 ()-]{5,18}[0-9]$/;
/**
 * Something typed as "password: X", "OTP = 123456", "recovery code: …". A
 * deliberately narrow pattern (label + ':' or '=' + a value) so ordinary
 * sentences such as "my password expired" are fine.
 */
const SECRET_RE = /\b(pass ?word|passwd|pwd|pass ?code|otp|one[- ]time code|mfa code|verification code|recovery code|security code|backup code)\s*[:=]\s*\S{3,}/i;
export const EMAIL_ISSUE_SECRET_MESSAGE = 'Never include passwords or verification codes. The IT team will never ask for them.';
/** True when a piece of free text looks like it contains a password or one-time code. */
export function looksLikeSecret(text) {
    return Boolean(text && SECRET_RE.test(text));
}
const EMAIL_FIELDS = new Set(['affectedEmail', 'sharedMailbox', 'senderEmail', 'recipientEmail']);
const TEXT_FIELDS = ['currentQuota', 'requestedQuota', 'businessJustification', 'reason', 'errorMessage'];
function present(v) {
    return typeof v === 'string' && v.trim() !== '';
}
/**
 * Validates the details. With `typeCode === undefined` only the format of what
 * was sent is checked (used by the zod schema, which cannot see the problem
 * type). With a type code (or `null` for "no type chosen") the per-type rules
 * apply too: required fields, refused fields, and the Other-description rule
 * when `description` is given. `{}` when valid. Messages are written for end users.
 */
export function validateEmailIssueDetails(d, typeCode, description) {
    const errors = {};
    const label = (f) => EMAIL_ISSUE_FIELD_LABELS[f];
    for (const f of EMAIL_ISSUE_FIELDS) {
        const v = d[f];
        if (!present(v))
            continue;
        const t = v.trim();
        if (EMAIL_FIELDS.has(f)) {
            if (t.length > 254 || !EMAIL_RE.test(t))
                errors[f] = 'Enter a valid email address.';
        }
        else if (f === 'newMobileNumber') {
            const digits = t.replace(/\D/g, '');
            if (!MOBILE_RE.test(t) || digits.length < 7 || digits.length > 15)
                errors[f] = 'Enter a valid mobile number, e.g. +91 98765 43210.';
        }
        else if (f === 'accessRequested') {
            if (!EMAIL_ISSUE_ACCESS_LEVELS.includes(t))
                errors[f] = 'Choose the access you need.';
        }
        else if (f === 'occurredAt') {
            const when = Date.parse(t);
            if (Number.isNaN(when))
                errors[f] = 'Enter a valid date and time.';
            else if (when > Date.now() + 5 * 60_000)
                errors[f] = 'The date and time cannot be in the future.';
        }
        else {
            const max = EMAIL_ISSUE_MAX_LENGTHS[f];
            if (t.length > max)
                errors[f] = `${label(f)} must be ${max} characters or fewer.`;
        }
        if (!errors[f] && TEXT_FIELDS.includes(f) && looksLikeSecret(t))
            errors[f] = EMAIL_ISSUE_SECRET_MESSAGE;
    }
    if (!present(d.affectedEmail))
        errors.affectedEmail = 'Enter the email address that has the problem.';
    if (typeCode !== undefined) {
        const allowed = new Set(['affectedEmail', ...emailIssueFieldsFor(typeCode)]);
        for (const f of EMAIL_ISSUE_FIELDS) {
            if (errors[f])
                continue;
            if (emailIssueFieldRequired(typeCode, f) && !present(d[f]))
                errors[f] = `${label(f)} is required.`;
            else if (!allowed.has(f) && present(d[f]))
                errors[f] = `${label(f)} does not apply to this type of problem.`;
        }
        if (description !== undefined) {
            const desc = description.trim();
            if (looksLikeSecret(desc))
                errors.description = EMAIL_ISSUE_SECRET_MESSAGE;
            else if (typeCode === 'OTHER' && desc.length < EMAIL_ISSUE_OTHER_MIN_DESCRIPTION) {
                errors.description = `Describe the problem in detail (at least ${EMAIL_ISSUE_OTHER_MIN_DESCRIPTION} characters).`;
            }
        }
    }
    return errors;
}
const optText = (max) => z.string().trim().max(max).nullish().transform((v) => (v ? v : null));
/**
 * The details on `POST /tickets`. `.strict()`: an unknown key (for example a
 * `password` field) is a 400, never silently dropped. Per-type rules run in
 * `CreateTicketService`, which knows the selected problem type.
 */
export const emailIssueDetailsSchema = z
    .object({
    affectedEmail: z.string().trim().max(254),
    currentQuota: optText(50),
    requestedQuota: optText(50),
    businessJustification: optText(1000),
    reason: optText(500),
    newMobileNumber: optText(20),
    sharedMailbox: optText(254),
    accessRequested: z.enum(EMAIL_ISSUE_ACCESS_LEVELS).nullish().transform((v) => v ?? null),
    senderEmail: optText(254),
    recipientEmail: optText(254),
    occurredAt: z.string().trim().max(40).nullish().transform((v) => (v ? v : null)),
    errorMessage: optText(4000),
})
    .strict()
    .superRefine((d, ctx) => {
    for (const [field, message] of Object.entries(validateEmailIssueDetails(d))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message });
    }
});
/** The stored details as returned on ticket detail (problem type is the ticket's subcategory). */
export const emailIssueDetailsRecordSchema = z.object({
    affectedEmail: z.string(),
    currentQuota: z.string().nullable(),
    requestedQuota: z.string().nullable(),
    businessJustification: z.string().nullable(),
    reason: z.string().nullable(),
    newMobileNumber: z.string().nullable(),
    sharedMailbox: z.string().nullable(),
    accessRequested: z.enum(EMAIL_ISSUE_ACCESS_LEVELS).nullable(),
    senderEmail: z.string().nullable(),
    recipientEmail: z.string().nullable(),
    occurredAt: z.string().nullable(),
    errorMessage: z.string().nullable(),
});
//# sourceMappingURL=email-issue.js.map